import { useQuery } from "@tanstack/react-query";

import { AZURE_MJ } from "@/lib/axios";
import {
  IProjectJobTypes,
  IProjectJobTypesRes,
  IProjectJobTypesListItem
} from "@/types/projectJobTypes";

export const PROJECT_JOB_TYPES_KEYS = {
  getAll: "PROJECT_JOB_TYPES_GET_ALL",
  getByOpportunityNo: "PROJECT_JOB_TYPES_GET_BY_OPPORTUNITY_NO"
};

/**
 * GET /project/job-types
 *
 * Every project's job types in one call, for joining against a project list by
 * `OpportunityNo` (the Projects Dashboard does exactly that). Use
 * `useGetProjectJobTypes` for a single project instead of filtering this.
 */
export const useGetAllProjectJobTypes = () => {
  return useQuery({
    queryKey: [PROJECT_JOB_TYPES_KEYS.getAll],
    queryFn: async (): Promise<IProjectJobTypesListItem[]> => {
      const { data } = await AZURE_MJ.get(`/project/job-types`);
      return data;
    },
    staleTime: 5 * 60 * 1000
  });
};

/**
 * GET /project/{opportunityNo}/job-types
 *
 * One project's job types. Returns the `JobType` object unwrapped, since the
 * opportunity number is already known at every call site.
 */
export const useGetProjectJobTypes = (opportunityNo?: string) => {
  return useQuery({
    queryKey: [PROJECT_JOB_TYPES_KEYS.getByOpportunityNo, opportunityNo],
    queryFn: async (): Promise<IProjectJobTypes | undefined> => {
      const { data } = await AZURE_MJ.get<IProjectJobTypesRes>(
        `/project/${encodeURIComponent(opportunityNo ?? "")}/job-type`
      );
      return data?.JobType;
    },
    enabled: !!opportunityNo,
    staleTime: 5 * 60 * 1000
  });
};
